/**
 * Surface 1 — Resident intake ("Report a Concern"). Implements intake/spec.md: one scrolling
 * column, two required fields (category + address), anonymous allowed, big taps, plain words.
 *
 * Validation is client-side via the SHARED zod schema (submitReportInputSchema, @td/shared) — the
 * exact rule the server enforces (NFR-11 / ADR-0010). On invalid submit an error summary lists each
 * problem and moves focus to it (WCAG 3.3.1). Submit is mocked and returns a tracking code.
 *
 * EMBED behavior (report-form-fullpage-embed — a town can embed `/report?embed=1&chrome=off` as a
 * direct iframe on its own page, not just via the widget's modal launcher): mirrors the
 * ContractorsDirectoryPage/MeetingsPage/FaqPage chromeless pattern exactly, three parts —
 *  1. `chromeless` (plain `?embed=1&chrome=off`, derived in App.tsx) hides this page's own H1 —
 *     TownHeader already renders nothing in ANY embed mode (see TownHeader.tsx), so this is the
 *     only extra suppression needed. Required-field labels, helper text, and the privacy link in
 *     the footer stay visible either way — nothing legally/functionally required is hidden.
 *  2. `useEmbedResize` posts `{type:"towndesk:report-resize", height}` to the parent on mount and
 *     whenever the observed root's rendered height changes — a validation-error summary appearing,
 *     an attachment row being added/removed, and the submit→confirmation swap are all covered for
 *     free because the ResizeObserver watches the single root that wraps the whole page.
 *  3. The iframe's own `<html>/<body>` are made transparent while chromeless (`.td-embed-
 *     transparent`), reverted on unmount — same gotcha/fix as the sibling pages.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  submitReportInputSchema,
  MAX_DESCRIPTION_LEN,
  INTAKE_SOURCES,
  type SubmitReportInput,
} from "@td/shared";
import { submitReport, ApiError } from "../api/index.js";
import { useGeolocation } from "../components/useGeolocation.js";
import { CategoryRadioGroup } from "../components/CategoryRadioGroup.js";
import { LocationField } from "../components/LocationField.js";
import { AttachmentUploader, type AttachmentTile } from "../components/AttachmentUploader.js";
import { ContactSection, type ContactValue } from "../components/ContactSection.js";
import { CaptchaField } from "../components/CaptchaField.js";
import { useCaptcha } from "../components/useCaptcha.js";
import { Field } from "../components/Field.js";
import { Button } from "../components/Button.js";
import { ErrorSummary, type FieldErrorRef } from "../components/ErrorSummary.js";
import { SpinnerIcon } from "../components/icons.js";
import { TownHeader } from "../components/TownHeader.js";
import { ResidentThemeRoot } from "../components/ResidentThemeRoot.js";
import { useTheme } from "../theme/ThemeProvider.js";
import { useDocumentTitle, pageTitle } from "../components/useDocumentTitle.js";
import { useEmbedClose } from "../components/useEmbedClose.js";
import { useEmbedResize } from "../components/useEmbedResize.js";

// Field ids (stable — the error summary links to these).
const ID = {
  category: "field-category",
  description: "field-description",
  address: "field-address",
  email: "field-contact-email",
  captcha: "field-captcha",
};

interface IntakePageProps {
  embed?: boolean;
  /** `?embed=1&chrome=off` only (App.tsx) — no-op when `embed` is false. Mirrors
   *  MeetingsPage/FaqPage/ConcernsMapPage/ContractorsDirectoryPage's chromeless convention. */
  chromeless?: boolean;
}

export function IntakePage({ embed = false, chromeless = false }: IntakePageProps): JSX.Element {
  const navigate = useNavigate();
  const isChromeless = embed && chromeless;
  const rootRef = useRef<HTMLDivElement>(null);
  // Posts `towndesk:report-resize` on mount and on every content-height change — a validation-
  // error summary appearing, an attachment row being added, and the submit→confirmation swap all
  // change the root's measured height, so the ResizeObserver watching it re-posts for free.
  useEmbedResize(embed, rootRef, "towndesk:report-resize");

  // CHROMELESS embed: `.td-intake--chromeless` makes our own container transparent, but the
  // iframe's own <body> still paints the cream `--td-paper` background (base.css) otherwise —
  // identical gotcha/fix as MeetingsPage/ConcernsMapPage/FaqPage/ContractorsDirectoryPage.
  // Reverted on unmount.
  useEffect(() => {
    if (!isChromeless) return;
    const { documentElement: html, body } = document;
    html.classList.add("td-embed-transparent");
    body.classList.add("td-embed-transparent");
    return () => {
      html.classList.remove("td-embed-transparent");
      body.classList.remove("td-embed-transparent");
    };
  }, [isChromeless]);

  const [searchParams] = useSearchParams();
  // Optional deep-link referral tag (e.g. the city Facebook page's action button →
  // /report?source=facebook). Allow-listed against INTAKE_SOURCES; anything else is ignored so an
  // arbitrary ?source= value never reaches the submit body (the server also re-normalizes it).
  const intakeSource = (() => {
    const raw = (searchParams.get("source") ?? "").trim().toLowerCase();
    return (INTAKE_SOURCES as readonly string[]).includes(raw)
      ? (raw as (typeof INTAKE_SOURCES)[number])
      : undefined;
  })();
  const { config, categories } = useTheme();
  useDocumentTitle(pageTitle("Report a Concern", config.townName));

  const [category, setCategory] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [address, setAddress] = useState("");
  // Reverse-geocoded address auto-fills the field (Lister's chosen UX) so the required-address
  // validation is satisfied and staff get a real address; coords still ride along either way.
  const geo = useGeolocation({ onAddress: setAddress });
  const [tiles, setTiles] = useState<AttachmentTile[]>([]);
  const [contact, setContact] = useState<ContactValue>({ name: "", email: "", phone: "" });
  const captcha = useCaptcha();

  const [summaryErrors, setSummaryErrors] = useState<FieldErrorRef[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitState, setSubmitState] = useState<"idle" | "submitting" | "error">("idle");
  const [submitErrorMsg, setSubmitErrorMsg] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const uploading = tiles.some((t) => t.status === "uploading");
  // The captcha is solved INLINE before submit (live) or via the fallback checkbox (local/mock), so
  // the button reflects captcha-ready in both modes.
  const requiredMissing = !category || !address.trim() || !captcha.passed;

  const dirty =
    Boolean(category) ||
    description.trim().length > 0 ||
    address.trim().length > 0 ||
    tiles.length > 0 ||
    Boolean(contact.name || contact.email || contact.phone);

  // In embed mode, answer the launcher's dirty-check so it can confirm "Discard this report?"
  // before closing (ADR-0004 postMessage contract; the launcher owns the modal + close).
  useEmbedClose(embed, dirty);

  /** Build the API input from form state (contact → undefined when fully blank = anonymous). */
  const buildInput = (): SubmitReportInput => {
    const trimmedContact = {
      name: contact.name.trim() || undefined,
      email: contact.email.trim() || undefined,
      phone: contact.phone.trim() || undefined,
    };
    const hasContact = trimmedContact.name || trimmedContact.email || trimmedContact.phone;
    return {
      category: category ?? "",
      description: description.trim(),
      location: {
        address: address.trim(),
        ...(geo.result ? { lat: geo.result.lat, lng: geo.result.lng } : {}),
      },
      attachments: tiles.filter((t) => t.status === "done" && t.key).map((t) => t.key as string),
      ...(hasContact ? { reporterContact: trimmedContact } : {}),
      ...(intakeSource ? { intakeSource } : {}),
    };
  };

  const helperForMissing = useMemo(() => {
    const missing: string[] = [];
    if (!category) missing.push("a category");
    if (!address.trim()) missing.push("an address");
    if (!captcha.passed) missing.push("the spam check");
    return missing;
  }, [category, address, captcha.passed]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitState === "submitting") return;

    const input = buildInput();
    const parsed = submitReportInputSchema.safeParse(input);

    if (!parsed.success) {
      const nextField: Record<string, string> = {};
      const summary: FieldErrorRef[] = [];
      for (const issue of parsed.error.issues) {
        const top = issue.path[0];
        const { id, msg } = mapIssue(String(top), issue.message);
        if (!nextField[id]) {
          nextField[id] = msg;
          summary.push({ targetId: id, message: msg });
        }
      }
      // Captcha isn't in the zod schema (server/edge concern). The puzzle is solved inline before
      // submit (live) or via the fallback checkbox (dev/test); either way `captcha.passed` gates it.
      if (!captcha.passed) {
        nextField[ID.captcha] = "Please complete the spam check.";
        summary.push({ targetId: ID.captcha, message: "Please complete the spam check." });
      }
      setFieldErrors(nextField);
      setSummaryErrors(summary);
      return;
    }

    if (!captcha.passed) {
      const msg = "Please complete the spam check.";
      setFieldErrors({ [ID.captcha]: msg });
      setSummaryErrors([{ targetId: ID.captcha, message: msg }]);
      return;
    }

    setFieldErrors({});
    setSummaryErrors([]);

    // Submit safety net: the inline solve token is reused when fresh (fast no-op); if it expired, the
    // puzzle re-challenges before the POST. The solved token rides the POST as the `x-aws-waf-token`
    // header (apiFetch — cookie-independent, so it also works in the cross-site embed iframe) so the
    // WAF `captcha-public-submit` rule passes it through. No-op when the live WAF is disabled.
    if (!(await captcha.ensureForSubmit())) {
      const msg = "We couldn't verify the spam check. Please try the puzzle again.";
      setSummaryErrors([{ targetId: ID.captcha, message: msg }]);
      return;
    }

    setSubmitState("submitting");
    setSubmitErrorMsg(null);
    try {
      const result = await submitReport(parsed.data);
      const categoryLabel =
        categories.find((c) => c.id === parsed.data.category)?.label ?? parsed.data.category;
      // Preserve `?embed=1&chrome=off` (and any other query params) across the navigate — App.tsx
      // derives `embed`/`chromeless` fresh from the URL on every route, so an embed submit that
      // dropped the search string here would silently lose chromeless/resize on /confirmation.
      navigate(`/confirmation${window.location.search}`, {
        state: {
          trackingCode: result.trackingCode,
          trackingUrl: result.trackingUrl,
          hasContact: Boolean(parsed.data.reporterContact),
          email: parsed.data.reporterContact?.email,
          category: categoryLabel,
          address: parsed.data.location.address,
        },
      });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Something went wrong submitting your report. Please try again.";
      setSubmitState("error");
      setSubmitErrorMsg(message);
    }
  };

  const submitLabel =
    submitState === "submitting"
      ? uploading
        ? "Finishing uploads…"
        : "Submitting…"
      : "Submit concern";

  return (
    <ResidentThemeRoot
      ref={rootRef}
      className={`td-intake${embed ? " td-intake--embed" : ""}${isChromeless ? " td-intake--chromeless" : ""}`}
    >
      {isChromeless ? null : (
        <a href="#td-report-form" className="td-skip-link">
          Skip to the report form
        </a>
      )}
      <TownHeader
        embed={embed}
        subcopy={`Report a concern to ${config.townName}. We'll follow up — you don't need an account.`}
      />

      <main className="td-intake__main">
        {isChromeless ? null : <h1 className="td-intake__title">Report a Concern</h1>}

        <ErrorSummary errors={summaryErrors} />

        {submitState === "error" && submitErrorMsg ? (
          <div className="td-alert td-alert--danger" role="alert">
            <p>{submitErrorMsg}</p>
            <Button variant="secondary" onClick={() => formRef.current?.requestSubmit()}>
              Try again
            </Button>
          </div>
        ) : null}

        <form
          id="td-report-form"
          ref={formRef}
          className="td-form"
          noValidate
          onSubmit={onSubmit}
          aria-busy={submitState === "submitting"}
        >
          <CategoryRadioGroup
            categories={categories}
            value={category}
            onChange={(id) => setCategory(id)}
            groupId={ID.category}
            error={fieldErrors[ID.category]}
          />

          <Field
            id={ID.description}
            label="Describe the concern"
            required
            helper="What did you see? Where about on the property, if it helps."
            error={fieldErrors[ID.description]}
          >
            {(aria) => (
              <>
                <textarea
                  {...aria}
                  className="td-input td-textarea"
                  rows={3}
                  maxLength={MAX_DESCRIPTION_LEN}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What did you see? Where about on the property, if it helps."
                />
                {description.length > 400 ? (
                  <span className="td-charcount" aria-live="polite">
                    {description.length} / {MAX_DESCRIPTION_LEN}
                  </span>
                ) : null}
              </>
            )}
          </Field>

          <LocationField
            address={address}
            onAddressChange={setAddress}
            addressError={fieldErrors[ID.address]}
            fieldId={ID.address}
            geo={geo}
          />

          <AttachmentUploader tiles={tiles} onChange={setTiles} />

          <ContactSection
            value={contact}
            onChange={setContact}
            emailError={fieldErrors[ID.email]}
          />

          <CaptchaField
            ref={captcha.containerRef}
            id={ID.captcha}
            status={captcha.status}
            error={captcha.error ?? undefined}
            passed={captcha.passed}
            onChange={(v) => {
              captcha.onChange(v);
              if (v && fieldErrors[ID.captcha]) {
                setFieldErrors((f) => {
                  const { [ID.captcha]: _drop, ...rest } = f;
                  return rest;
                });
              }
            }}
            onRetry={captcha.retry}
          />

          <div className="td-submit">
            <Button
              type="submit"
              fullWidth
              disabled={submitState === "submitting"}
              aria-disabled={requiredMissing || submitState === "submitting"}
            >
              {submitState === "submitting" ? (
                <>
                  <SpinnerIcon width={18} height={18} />
                  {submitLabel}
                </>
              ) : (
                submitLabel
              )}
            </Button>
            {requiredMissing && submitState !== "submitting" ? (
              <p className="td-submit__hint" aria-live="polite">
                Still needed: {helperForMissing.join(", ")}.
              </p>
            ) : null}
          </div>
        </form>
      </main>

      <footer className="td-footer">
        <span>
          Powered by{" "}
          <a href="https://towndesk.us" target="_blank" rel="noopener noreferrer">
            TownDesk
          </a>
        </span>
        {config.privacyUrl ? (
          <a href={config.privacyUrl} rel="noopener">
            Privacy
          </a>
        ) : null}
      </footer>
    </ResidentThemeRoot>
  );
}

/** Map a zod issue path → the field id + a resident-friendly message. */
function mapIssue(path: string, zodMessage: string): { id: string; msg: string } {
  switch (path) {
    case "category":
      return { id: ID.category, msg: "Choose what you're reporting." };
    case "description":
      return { id: ID.description, msg: "Add a short description of the concern." };
    case "location":
      return { id: ID.address, msg: "Enter an address or landmark." };
    case "reporterContact":
      return { id: ID.email, msg: "Enter a valid email, or leave contact blank." };
    default:
      return { id: ID.description, msg: zodMessage };
  }
}
